import { Injectable } from '@angular/core';
import { FundDetailService } from '@components/products/services/fund-detail.service';
import {
  FundPerformanceAnnualizedState,
  FundPerformanceSIPRowName,
  FundPerformanceTableData,
  ProductDetailConfiguration,
} from '@types';
import { Observable, Subject } from 'rxjs';
import FundPerformanceAnnualizedQuery from '@graphql/fund-performance/fund-performance-annualized.graphql';
import {
  BMPerformanceDetail,
  PerformanceDetail,
  Product,
  ShareClass,
} from '@components/products/models';
import { Logger } from '@utils/logger';
import { hasValue } from '@components/products/utils/mappers/mapper.utils';
import { DashIfEmptyPipe } from '@components/shared/pipes/dash-if-empty.pipe';
import { TranslateService } from '@components/shared/translate/translate.service';
import { InrSymbolPipe } from '@components/shared/pipes/inr-symbol.pipe';

const logger = Logger.getLogger('FundPerformanceAnnualizedService');

@Injectable({
  providedIn: 'root',
})
export class FundPerformanceAnnualizedService {
  private state: FundPerformanceAnnualizedState;
  private annualizedState$: Subject<FundPerformanceAnnualizedState>;
  private showSipData$: Subject<boolean>;

  formatter = new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: 0,
    style: 'currency',
    currency: 'INR',
  });

  constructor(
    private fundDetailService: FundDetailService,
    private dashIfEmptyPipe: DashIfEmptyPipe,
    private inrSymbolPipe: InrSymbolPipe,
    private translateService: TranslateService
  ) {
    this.state = {
      perfAsOfDate: null,
      sinceInceptionReturnDate: null,
      lumpsumData: {
        annualized: {
          performance: null,
          bmPerformance: null,
        },
        discrete: {
          performance: null,
          bmPerformance: null,
        },
        cumulative: {
          performance: null,
          bmPerformance: null,
        },
        annualizedCurrent: {
          performance: null,
          bmPerformance: null,
        },
      },
      sipData: null,
    };
    this.annualizedState$ = new Subject();
    this.showSipData$ = new Subject();
  }

  /**
   * Return fund performance annualized state as observable.
   */
  getAnnualizedState$(): Observable<FundPerformanceAnnualizedState> {
    return this.annualizedState$.asObservable();
  }

  /**
   * Return SIP data visibility update as observable.
   */
  getSipDataVisibilityUpdate$(): Observable<boolean> {
    return this.showSipData$.asObservable();
  }

  /**
   * Set SIP data visibility as boolean state received.
   * @param showSip boolean state received for toggle button
   */
  setSipDataVisibility(showSip: boolean): void {
    this.showSipData$.next(showSip);
  }

  /**
   * Fetch the fund annualized performance from PDS and initialize state.
   * @param productDetailConfiguration product detail configuration from bloomreach
   */
  populate(productDetailConfiguration: ProductDetailConfiguration): void {
    this.state.showDiscretePerformance =
      productDetailConfiguration.fundId === '4629';

    // Fetch an API response, if its the first load or the fund has been changed.
    this.fundDetailService
      .register(FundPerformanceAnnualizedQuery, {
        fundId: productDetailConfiguration.fundId,
      })
      .subscribe(
        (fundDetail: Product) => {
          logger.debug(`Mapped fund detail response: ${fundDetail}`);

          // Extract primary share class, since the same will have data for performances.
          const primaryShareClass: ShareClass = fundDetail.shareClasses?.find(
            (shareClass) => shareClass.primaryShareClass
          );

          if (primaryShareClass) {
            const monthEndPerformances: PerformanceDetail[] =
              primaryShareClass.performance.monthEnd;
            const monthEndBMPerformances: BMPerformanceDetail[] =
              primaryShareClass.benchmarkPerformance.monthEnd;

            this.state.perfAsOfDate = monthEndPerformances?.find(
              (calc) => calc.calcTypeStd === 'NET'
            )?.performanceAsOfDate;
            this.state.sinceInceptionReturnDate =
              primaryShareClass.inceptionDate;

            // Map lumpsum performance data to state.
            this.mapLumpsumDataToState(
              monthEndPerformances,
              monthEndBMPerformances
            );

            // Map SIP performance data to state.
            this.state.sipData = this.mapSipDataToState(
              monthEndPerformances,
              monthEndBMPerformances
            );

            this.state.isLoaded = true;
          } else {
            this.state.isLoaded = false;
          }

          this.annualizedState$.next(this.state);
        },
        (error) => {
          logger.error(`Error in invoking fund detail service: ${error}`);

          this.annualizedState$.error(error);
        }
      );
  }

  /**
   * Extract lumpsum performance data for both annualized and cumulative and assign it accordingly to state.
   * @param monthEndPerformances month end performance detail
   * @param monthEndBMPerformances month end benchmark performance detail
   */
  private mapLumpsumDataToState(
    monthEndPerformances: PerformanceDetail[],
    monthEndBMPerformances: BMPerformanceDetail[]
  ): void {
    const monthEndPerformanceAnnualized: PerformanceDetail = monthEndPerformances.find(
      (perfDetail) => perfDetail.calcTypeStd.toUpperCase() === 'NET'
    );
    const monthEndPerformanceCumulative: PerformanceDetail = monthEndPerformances.find(
      (perfDetail) => perfDetail.calcTypeStd.toUpperCase() === 'TOTAL_VALUE'
    );

    const monthEndPerformanceAnnualizedCurrent: PerformanceDetail = monthEndPerformances.find(
      (perfDetail) => perfDetail.calcTypeStd.toUpperCase() === 'LUMPSUM_NET'
    );

    // Assign lumpsum annualized performance data, if present.
    if (monthEndPerformanceAnnualized) {
      this.state.lumpsumData.annualized.performance = this.formatLumpsumTableData(
        monthEndPerformanceAnnualized,
        true
      );

      if (this.state.showDiscretePerformance) {
        this.state.lumpsumData.discrete.performance = this.formatDiscreteLumpsumTableData(
          monthEndPerformanceAnnualized
        );
      }
    }

    // Assign lumpsum annualized current performance data, if present.
    if (monthEndPerformanceAnnualizedCurrent) {
      this.state.lumpsumData.annualizedCurrent.performance = this.formatLumpsumCurrentTableData(
        monthEndPerformanceAnnualizedCurrent,
        true
      );
    }

    // Assign lumpsum cumulative performance data, if present.
    if (monthEndPerformanceCumulative) {
      this.state.lumpsumData.cumulative.performance = this.formatLumpsumTableData(
        monthEndPerformanceCumulative,
        false
      );
    }

    // Assign lumpsum benchmark performance data, if present.
    if (monthEndBMPerformances.length) {
      this.state.lumpsumData.annualized.bmPerformance = [];
      this.state.lumpsumData.discrete.bmPerformance = [];
      this.state.lumpsumData.cumulative.bmPerformance = [];
      this.state.lumpsumData.annualizedCurrent.bmPerformance = [];

      monthEndBMPerformances
        .sort(
          (a, b) => a.benchmarkLabel.localeCompare(b.benchmarkLabel) // Insta1 should be first; ascending order.
        )
        .forEach((monthEndBMPerformance) => {
          if (monthEndBMPerformance.calcTypeStd.toUpperCase() === 'NET') {
            this.state.lumpsumData.annualized.bmPerformance.push(
              this.formatLumpsumTableData(monthEndBMPerformance, true)
            );

            if (this.state.showDiscretePerformance) {
              this.state.lumpsumData.discrete.bmPerformance.push(
                this.formatDiscreteLumpsumTableData(monthEndBMPerformance)
              );
            }
          }

          if (monthEndBMPerformance.calcTypeStd.toUpperCase() === 'LUMPSUM_NET') {
            this.state.lumpsumData.annualizedCurrent.bmPerformance.push(
              this.formatLumpsumCurrentTableData(monthEndBMPerformance, true)
            );
          }

          if (monthEndBMPerformance.calcTypeStd === 'TOTAL_VALUE') {
            this.state.lumpsumData.cumulative.bmPerformance.push(
              this.formatLumpsumTableData(monthEndBMPerformance, false)
            );
          }
        });
    }
  }

  private formatLumpsumCurrentTableData(
    performanceDetail: PerformanceDetail | BMPerformanceDetail,
    isPerformanceAnnualized: boolean
  ): FundPerformanceTableData {
    return {
      bmLabel: isPerformanceAnnualized ? (performanceDetail as BMPerformanceDetail).benchmarkLabel : null,
      bmName: isPerformanceAnnualized
        ? (performanceDetail as BMPerformanceDetail).benchmarkName
        : null,
      year1Return: this.formatLumpsumValue(
        performanceDetail,
        isPerformanceAnnualized
          ? 'cummulativeReturn1Year'
          : 'avgAnnual1YearReturn',
        isPerformanceAnnualized
      ),
      year3Return: this.formatLumpsumValue(
        performanceDetail,
        'cummulativeReturn3Year',
        isPerformanceAnnualized
      ),
      year5Return: this.formatLumpsumValue(
        performanceDetail,
        'cummulativeReturn5Year',
        isPerformanceAnnualized
      ),
      year10Return: this.formatLumpsumValue(
        performanceDetail,
        'cummulativeReturn10Year',
        isPerformanceAnnualized
      ),
      year15Return: this.formatLumpsumValue(
        performanceDetail,
        'cummulativeReturn15Year',
        isPerformanceAnnualized
      ),
      sinceInceptionReturn: this.formatLumpsumValue(
        performanceDetail,
        'cummulativeReturnSinceInception',
        isPerformanceAnnualized
      ),
      year1CurrentReturn: this.formatLumpsumCurrentValue(
        performanceDetail,
        isPerformanceAnnualized
          ? 'avgAnnual1YearReturn'
          : 'cummulativeReturn1Year',
        isPerformanceAnnualized
      ),
      year3CurrentReturn: this.formatLumpsumCurrentValue(
        performanceDetail,
        isPerformanceAnnualized
          ? 'avgAnnual3YearReturn'
          : 'cummulativeReturn3Year',
        isPerformanceAnnualized
      ),
      year5CurrentReturn: this.formatLumpsumCurrentValue(
        performanceDetail,
        isPerformanceAnnualized
          ? 'avgAnnual5YearReturn'
          : 'cummulativeReturn5Year',
        isPerformanceAnnualized
      ),
      year10CurrentReturn: this.formatLumpsumCurrentValue(
        performanceDetail,
        isPerformanceAnnualized
          ? 'avgAnnual10YearReturn'
          : 'cummulativeReturn10Year',
        isPerformanceAnnualized
      ),
      year15CurrentReturn: this.formatLumpsumCurrentValue(
        performanceDetail,
        isPerformanceAnnualized
          ? 'avgAnnual15YearReturn'
          : 'cummulativeReturn15Year',
        isPerformanceAnnualized
      ),
      sinceInceptionCurrentReturn: this.formatLumpsumCurrentValue(
        performanceDetail,
        isPerformanceAnnualized
          ? 'avgAnnualSinceInceptionReturn'
          : 'cummulativeReturnSinceInception',
        isPerformanceAnnualized
      ),
    };
  }

  /**
   * Create table data structure for Lumpsum performance (annualized/cumulative) and return it.
   * @param performanceDetail month end performance detail
   * @param isPerformanceAnnualized boolean flag if the performance data is annualized
   */
  private formatLumpsumTableData(
    performanceDetail: PerformanceDetail | BMPerformanceDetail,
    isPerformanceAnnualized: boolean
  ): FundPerformanceTableData {
    return {
      bmLabel: isPerformanceAnnualized ? (performanceDetail as BMPerformanceDetail).benchmarkLabel : null,
      bmName: isPerformanceAnnualized
        ? (performanceDetail as BMPerformanceDetail).benchmarkName
        : null,
      year1Return: this.formatLumpsumValue(
        performanceDetail,
        isPerformanceAnnualized
          ? 'avgAnnual1YearReturn'
          : 'cummulativeReturn1Year',
        isPerformanceAnnualized
      ),
      year3Return: this.formatLumpsumValue(
        performanceDetail,
        isPerformanceAnnualized
          ? 'avgAnnual3YearReturn'
          : 'cummulativeReturn3Year',
        isPerformanceAnnualized
      ),
      year5Return: this.formatLumpsumValue(
        performanceDetail,
        isPerformanceAnnualized
          ? 'avgAnnual5YearReturn'
          : 'cummulativeReturn5Year',
        isPerformanceAnnualized
      ),
      year10Return: this.formatLumpsumValue(
        performanceDetail,
        isPerformanceAnnualized
          ? 'avgAnnual10YearReturn'
          : 'cummulativeReturn10Year',
        isPerformanceAnnualized
      ),
      year15Return: this.formatLumpsumValue(
        performanceDetail,
        isPerformanceAnnualized
          ? 'avgAnnual15YearReturn'
          : 'cummulativeReturn15Year',
        isPerformanceAnnualized
      ),
      sinceInceptionReturn: this.formatLumpsumValue(
        performanceDetail,
        isPerformanceAnnualized
          ? 'avgAnnualSinceInceptionReturn'
          : 'cummulativeReturnSinceInception',
        isPerformanceAnnualized
      ),
    };
  }

  private formatDiscreteLumpsumTableData(
    performanceDetail: PerformanceDetail | BMPerformanceDetail
  ): FundPerformanceTableData {
    return {
      bmName: (performanceDetail as BMPerformanceDetail).benchmarkName,
      year1Return: this.formatLumpsumValue(
        performanceDetail,
        'discreteAnnualRollingYear1',
        true
      ),
      year2Return: this.formatLumpsumValue(
        performanceDetail,
        'discreteAnnualRollingYear2',
        true
      ),
      year3Return: this.formatLumpsumValue(
        performanceDetail,
        'discreteAnnualRollingYear3',
        true
      ),
      year4Return: this.formatLumpsumValue(
        performanceDetail,
        'discreteAnnualRollingYear4',
        true
      ),
      year5Return: this.formatLumpsumValue(
        performanceDetail,
        'discreteAnnualRollingYear5',
        true
      ),
    };
  }
  /**
   * Extract performance value as per property name from performance detail object.
   * Also, add '%' symbol to values for annualized performance.
   * Finally, add Emdash if value is still empty.
   * @param performanceDetail month end performance detail
   * @param propName property name in performance detail object
   * @param isPerformanceAnnualized boolean flag if the performance data is annualized
   */
  private formatLumpsumValue(
    performanceDetail: PerformanceDetail,
    propName: keyof PerformanceDetail,
    isPerformanceAnnualized: boolean
  ): string {
    let parsedValue: string;

    if (hasValue(performanceDetail, propName)) {
      parsedValue = performanceDetail[propName];

      if (isPerformanceAnnualized) {
        parsedValue += '%';
      }
    }

    return this.dashIfEmptyPipe.transform(parsedValue);
  }

  /**
   * Extract performance value as per property name from performance detail object.
   * Also, add '%' symbol to values for annualized performance.
   * Finally, add Emdash if value is still empty.
   * @param performanceDetail month end performance detail
   * @param propName property name in performance detail object
   * @param isPerformanceAnnualized boolean flag if the performance data is annualized
   */
  private formatLumpsumCurrentValue(
    performanceDetail: PerformanceDetail,
    propName: keyof PerformanceDetail,
    isPerformanceAnnualized: boolean
  ): string {
    let parsedValue: string;
    if (hasValue(performanceDetail, propName)) {
      parsedValue = performanceDetail[propName];
      if (isPerformanceAnnualized) {
        parsedValue = this.formatter.format(parseInt(parsedValue));
      }
    }

    return this.dashIfEmptyPipe.transform(parsedValue);
  }

  /**
   * Extract SIP performance data and assign it accordingly to state.
   * @param monthEndPerformances month end performance detail
   * @param monthEndBMPerformances month end benchmark performance detail
   */
  private mapSipDataToState(
    monthEndPerformances: PerformanceDetail[],
    monthEndBMPerformances: BMPerformanceDetail[]
  ): FundPerformanceTableData[] {
    const sipData: FundPerformanceTableData[] = [];

    const sipdataCAGRData: PerformanceDetail = monthEndPerformances.find(
      (monthPerformance) => monthPerformance.calcTypeStd.toUpperCase() === 'SIP_NET'
    );
    if (sipdataCAGRData) {
      sipData.push(
        this.formatSipTableData(
          sipdataCAGRData,
          FundPerformanceSIPRowName.SIP_NET,
        )
      );
    }

    const totalInvestedPerfData: PerformanceDetail = monthEndPerformances.find(
      (monthEndPerformance) =>
        monthEndPerformance.calcTypeStd.toUpperCase() === 'TOTAL_INVESTED'
    );
    if (totalInvestedPerfData) {
      sipData.push(
        this.formatSipTableData(
          totalInvestedPerfData,
          FundPerformanceSIPRowName.PERF_TOTAL_INVESTED
        )
      );
    }

    const totalValuePerfData: PerformanceDetail = monthEndPerformances.find(
      (monthEndPerformance) =>
        monthEndPerformance.calcTypeStd.toUpperCase() === 'TOTAL_VALUE'
    );
    if (totalValuePerfData) {
      sipData.push(
        this.formatSipTableData(
          totalValuePerfData,
          FundPerformanceSIPRowName.PERF_TOTAL_VALUE
        )
      );
    }

    const returnsPerfData: PerformanceDetail = monthEndPerformances.find(
      (monthEndPerformance) =>
        monthEndPerformance.calcTypeStd.toUpperCase() === 'RETURNS'
    );
    if (returnsPerfData) {
      sipData.push(
        this.formatSipTableData(
          returnsPerfData,
          FundPerformanceSIPRowName.PERF_RETURNS
        )
      );
    }

    const sipDataBMPerfData: BMPerformanceDetail = monthEndBMPerformances.find(
      (monthEndBMPerformance) =>
        monthEndBMPerformance.calcTypeStd.toUpperCase() === 'SIP_NET' &&
        monthEndBMPerformance.benchmarkLabel.toUpperCase() === 'INST1'
    );
    if (sipDataBMPerfData) {
      sipData.push(
        this.formatSipTableData(
          sipDataBMPerfData,
          // FundPerformanceSIPRowName.BM_PERF_TOTAL_VALUE_INSTA_1,
          FundPerformanceSIPRowName.BM_PERF_RETURNS_INSTA_1,
          true
        )
      );
    }

    const sipData2BMPerfData: BMPerformanceDetail = monthEndBMPerformances.find(
      (monthEndBMPerformance) =>
        monthEndBMPerformance.calcTypeStd.toUpperCase() === 'SIP_NET' &&
        monthEndBMPerformance.benchmarkLabel.toUpperCase() === 'INST2'
    );
    if (sipData2BMPerfData) {
      sipData.push(
        this.formatSipTableData(
          sipData2BMPerfData,
          // FundPerformanceSIPRowName.BM_PERF_TOTAL_VALUE_INSTA_1,
          FundPerformanceSIPRowName.BM_PERF_RETURNS_INSTA_2,
          true
        )
      );
    }

    const totalValueInsta1BMPerfData: BMPerformanceDetail = monthEndBMPerformances.find(
      (monthEndBMPerformance) =>
        monthEndBMPerformance.calcTypeStd.toUpperCase() === 'TOTAL_VALUE' &&
        monthEndBMPerformance.benchmarkLabel.toUpperCase() === 'INST1'
    );
    if (totalValueInsta1BMPerfData) {
      sipData.push(
        this.formatSipTableData(
          totalValueInsta1BMPerfData,
          FundPerformanceSIPRowName.BM_PERF_TOTAL_VALUE_INSTA_1,
          true
        )
      );
    }

    const totalValueInsta2BMPerfData: BMPerformanceDetail = monthEndBMPerformances.find(
      (monthEndBMPerformance) =>
        monthEndBMPerformance.calcTypeStd.toUpperCase() === 'TOTAL_VALUE' &&
        monthEndBMPerformance.benchmarkLabel.toUpperCase() === 'INST2'
    );
    if (totalValueInsta2BMPerfData) {
      sipData.push(
        this.formatSipTableData(
          totalValueInsta2BMPerfData,
          FundPerformanceSIPRowName.BM_PERF_TOTAL_VALUE_INSTA_2,
          true
        )
      );
    }

    const returnsInsta1BMPerfData: BMPerformanceDetail = monthEndBMPerformances.find(
      (monthEndBMPerformance) =>
        monthEndBMPerformance.calcTypeStd.toUpperCase() === 'RETURNS' &&
        monthEndBMPerformance.benchmarkLabel.toUpperCase() === 'INST1'
    );
    if (returnsInsta1BMPerfData) {
      sipData.push(
        this.formatSipTableData(
          returnsInsta1BMPerfData,
          FundPerformanceSIPRowName.BM_PERF_RETURNS_INSTA_1,
          true
        )
      );
    }

    const returnsInsta2BMPerfData: BMPerformanceDetail = monthEndBMPerformances.find(
      (monthEndBMPerformance) =>
        monthEndBMPerformance.calcTypeStd.toUpperCase() === 'RETURNS' &&
        monthEndBMPerformance.benchmarkLabel.toUpperCase() === 'INST2'
    );
    if (returnsInsta2BMPerfData) {
      sipData.push(
        this.formatSipTableData(
          returnsInsta2BMPerfData,
          FundPerformanceSIPRowName.BM_PERF_RETURNS_INSTA_2,
          true
        )
      );
    }

    return sipData;
  }

  /**
   * Create table data structure for SIP performance and return it.
   * @param performanceDetail month end performance detail
   * @param sipRowName enum value for SIP row name
   * @param isBenchmarkData boolean flag if the performance is benchmark data
   */
  private formatSipTableData(
    performanceDetail: PerformanceDetail | BMPerformanceDetail,
    sipRowName: FundPerformanceSIPRowName,
    isBenchmarkData?: boolean
  ): FundPerformanceTableData {
    return {
      bmName: this.getSipTableBMName(
        sipRowName,
        isBenchmarkData
          ? (performanceDetail as BMPerformanceDetail).benchmarkName
          : null
      ),
      year1Return: this.formatSipValue(
        performanceDetail,
        'cummulativeReturn1Year',
        sipRowName
      ),
      year3Return: this.formatSipValue(
        performanceDetail,
        'cummulativeReturn3Year',
        sipRowName
      ),
      year5Return: this.formatSipValue(
        performanceDetail,
        'cummulativeReturn5Year',
        sipRowName
      ),
      year10Return: this.formatSipValue(
        performanceDetail,
        'cummulativeReturn10Year',
        sipRowName
      ),
      year15Return: this.formatSipValue(
        performanceDetail,
        'cummulativeReturn15Year',
        sipRowName
      ),
      sinceInceptionReturn: this.formatSipValue(
        performanceDetail,
        'cummulativeReturnSinceInception',
        sipRowName
      ),
      year1CurrentReturn: this.formatSipValue(
        performanceDetail,
        'avgAnnual1YearReturn',
        FundPerformanceSIPRowName.PERF_TOTAL_INVESTED
      ),
      year3CurrentReturn: this.formatSipValue(
        performanceDetail,
        'avgAnnual3YearReturn',
        FundPerformanceSIPRowName.PERF_TOTAL_INVESTED
      ),
      year5CurrentReturn: this.formatSipValue(
        performanceDetail,
        'avgAnnual5YearReturn',
        FundPerformanceSIPRowName.PERF_TOTAL_INVESTED
      ),
      year10CurrentReturn: this.formatSipValue(
        performanceDetail,
        'avgAnnual10YearReturn',
        FundPerformanceSIPRowName.PERF_TOTAL_INVESTED
      ),
      year15CurrentReturn: this.formatSipValue(
        performanceDetail,
        'avgAnnual15YearReturn',
        FundPerformanceSIPRowName.PERF_TOTAL_INVESTED
      ),
      sinceInceptionCurrentReturn: this.formatSipValue(
        performanceDetail,
        'avgAnnualSinceInceptionReturn',
        FundPerformanceSIPRowName.PERF_TOTAL_INVESTED
      ),
    };
  }

  /**
   * Get the appropriate label for bm name.
   * @param sipRowName enum value for SIP row name
   * @param bmname benchmark name of benchmark performance record
   */
  private getSipTableBMName(
    sipRowName: FundPerformanceSIPRowName,
    bmname: string
  ): string {
    switch (sipRowName) {
      case FundPerformanceSIPRowName.PERF_TOTAL_INVESTED:
        return this.translateService.instant(
          'products.performance-sip-total-amount-invested'
        );
      case FundPerformanceSIPRowName.PERF_TOTAL_VALUE:
        return `${this.translateService.instant(
          'products.performance-sip-total-value'
        )} ${this.state.perfAsOfDate}`;
      case FundPerformanceSIPRowName.PERF_RETURNS:
        return this.translateService.instant(
          'products.performance-sip-returns'
        );
      case FundPerformanceSIPRowName.BM_PERF_TOTAL_VALUE_INSTA_1:
        return `${this.translateService.instant(
          'products.performance-sip-bm-total-value-insta-one'
        )}: ${bmname}`;
      case FundPerformanceSIPRowName.BM_PERF_TOTAL_VALUE_INSTA_2:
        return `${this.translateService.instant(
          'products.performance-sip-bm-total-value-insta-two'
        )}: ${bmname}`;
      case FundPerformanceSIPRowName.BM_PERF_RETURNS_INSTA_1:
        return `${bmname}`;
        // `${this.translateService.instant(
        //   'products.performance-sip-bm-returns-insta-one'
        // )}: ${bmname} ${this.translateService.instant(
        //   'products.performance-sip-returns'
        // )}`;
      case FundPerformanceSIPRowName.BM_PERF_RETURNS_INSTA_2:
        return `${bmname}`
        // `${this.translateService.instant(
        //   'products.performance-sip-bm-returns-insta-two'
        // )}: ${bmname} ${this.translateService.instant(
        //   'products.performance-sip-returns'
        // )}`;
      case FundPerformanceSIPRowName.SIP_NET:
        return `${this.translateService.instant(
          'products.performance-annualized-col-header-fund'
        )}`;
    }
  }

  /**
   * Extract performance value as per property name from performance detail object.
   * Also, add '₹' or '%' symbol to values as per SIP row name.
   * Finally, add Emdash if value is still empty.
   * @param performanceDetail month end performance detail
   * @param propName property name in performance detail object
   * @param sipRowName enum value for SIP row name
   */
  private formatSipValue(
    performanceDetail: PerformanceDetail,
    propName: keyof PerformanceDetail,
    sipRowName: FundPerformanceSIPRowName
  ): string {
    let parsedValue: string;
    if (hasValue(performanceDetail, propName)) {
      parsedValue = performanceDetail[propName];
      if (
        sipRowName === FundPerformanceSIPRowName.PERF_RETURNS ||
        sipRowName === FundPerformanceSIPRowName.BM_PERF_RETURNS_INSTA_1 ||
        sipRowName === FundPerformanceSIPRowName.BM_PERF_RETURNS_INSTA_2 ||
        sipRowName === FundPerformanceSIPRowName.SIP_NET
      ) {
        parsedValue += '%';
      } else {
        parsedValue = this.formatter.format(parseInt(parsedValue));
        //parsedValue = this.inrSymbolPipe.transform(parsedValue, 'pre');
      }
    }

    return this.dashIfEmptyPipe.transform(parsedValue);
  }
}
